
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, ExternalLink, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { SubscriptionService } from "@/services/subscription";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";

export const PricingSection = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState<'monthly' | 'yearly' | null>(null);
  
  const handlePlanSelection = async (type: 'monthly' | 'yearly' | 'free') => {
    if (type === 'free') {
      navigate("/auth?mode=signup");
      return;
    }

    setIsLoading(true);
    setProcessingPlan(type);
    
    try {
      // Get the current user from Supabase
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/auth?mode=signup");
        return;
      }

      console.log(`Creating ${type} checkout for user: ${user.id}`);
      const checkoutUrl = await SubscriptionService.createCheckoutSession(user.id, type);
      
      if (checkoutUrl) {
        console.log('Received checkout URL:', checkoutUrl);
        
        // Store selected plan
        localStorage.setItem('selected_plan', type === 'monthly' ? 'Monthly' : 'Yearly');
        
        // Try to open in a new tab
        const checkoutWindow = window.open(checkoutUrl, '_blank');
        
        // If popup was blocked or failed to open
        if (!checkoutWindow) {
          toast({
            title: "Popup Blocked",
            description: "We couldn't open the checkout page automatically. Please click the button below to continue.",
            action: (
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2 w-full flex items-center justify-center gap-2"
                onClick={() => window.open(checkoutUrl, '_blank')}
              >
                Open Checkout <ExternalLink size={16} />
              </Button>
            ),
            duration: 10000, // Longer duration so user has time to see and click
          });
        }
      } else {
        throw new Error('No checkout URL received from payment processor');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to start checkout process. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setProcessingPlan(null);
    }
  };
  
  return (
    <motion.div 
      className="mt-20 text-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.6 }}
    >
      <h2 className="text-4xl font-bold mb-8 text-white">Choose Your Plan</h2>
      <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto px-4">
        {/* Free Plan */}
        <motion.div 
          className="bg-white/90 backdrop-blur-sm p-8 rounded-xl shadow-xl"
          whileHover={{ y: -5, scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <h3 className="text-2xl font-bold mb-2">Free</h3>
          <p className="text-4xl font-bold mb-6">$0</p>
          <ul className="text-left space-y-4 mb-8">
            <li className="flex items-center">
              <Check className="w-5 h-5 text-primary mr-2" />
              <span>3 Recipe Transformations</span>
            </li>
            <li className="flex items-center">
              <Check className="w-5 h-5 text-primary mr-2" />
              <span>Basic Science Facts</span>
            </li>
            <li className="flex items-center">
              <Check className="w-5 h-5 text-primary mr-2" />
              <span>Safety Tips</span>
            </li>
          </ul>
          <Button 
            onClick={() => handlePlanSelection('free')}
            variant="outline"
            className="w-full"
            disabled={isLoading}
          >
            Get Started
          </Button>
        </motion.div>

        {/* Monthly Plan */}
        <motion.div 
          className="bg-primary p-8 rounded-xl shadow-xl text-white relative"
          whileHover={{ y: -5, scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="bg-accent text-accent-foreground px-4 py-1 rounded-full text-sm font-medium">
              Most Popular
            </span>
          </div>
          <h3 className="text-2xl font-bold mb-2">Monthly</h3>
          <p className="text-4xl font-bold mb-6">$5</p>
          <ul className="text-left space-y-4 mb-8">
            <li className="flex items-center">
              <Check className="w-5 h-5 mr-2" />
              <span>Unlimited Transformations</span>
            </li>
            <li className="flex items-center">
              <Check className="w-5 h-5 mr-2" />
              <span>Advanced Science Facts</span>
            </li>
            <li className="flex items-center">
              <Check className="w-5 h-5 mr-2" />
              <span>Interactive Safety Tips</span>
            </li>
            <li className="flex items-center">
              <Check className="w-5 h-5 mr-2" />
              <span>Progress Tracking</span>
            </li>
          </ul>
          <Button 
            onClick={() => handlePlanSelection('monthly')}
            className="w-full bg-white text-primary hover:bg-white/90"
            disabled={isLoading}
          >
            {processingPlan === 'monthly' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : "Choose Monthly"}
          </Button>
        </motion.div>

        {/* Yearly Plan */}
        <motion.div 
          className="bg-white/90 backdrop-blur-sm p-8 rounded-xl shadow-xl"
          whileHover={{ y: -5, scale: 1.02 }}
          transition={{ duration: 0.2 }}
        >
          <h3 className="text-2xl font-bold mb-2">Yearly</h3>
          <p className="text-4xl font-bold mb-6">$50</p>
          <div className="text-sm text-primary mb-4">Save $10!</div>
          <ul className="text-left space-y-4 mb-8">
            <li className="flex items-center">
              <Check className="w-5 h-5 text-primary mr-2" />
              <span>Everything in Monthly</span>
            </li>
            <li className="flex items-center">
              <Check className="w-5 h-5 text-primary mr-2" />
              <span>Priority Support</span>
            </li>
            <li className="flex items-center">
              <Check className="w-5 h-5 text-primary mr-2" />
              <span>Early Access Features</span>
            </li>
          </ul>
          <Button 
            onClick={() => handlePlanSelection('yearly')}
            variant="outline"
            className="w-full"
            disabled={isLoading}
          >
            {processingPlan === 'yearly' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : "Choose Yearly"}
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
};
